'use client';

import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { modal } from '@federa/components/css-refs.layout';
import { borderRadius, boxShadow, motion } from '@federa/components/css-refs.effects';
import { FLEX_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { ButtonWithIcon } from '../primitives/ButtonWithIcon.tsx';
import type { CarouselNavButtonProps } from '@federa/types/ui-carousel-nav';

/**
 * THE carousel prev/next arrow -- one look everywhere an image strip is paged: the explore property card, the mobile
 * feed card and the property page's mobile carousel (operator directive 2026-09-02: the same arrows, the same
 * roundness, everywhere). A frosted circle, gray chevron, vertically centred over the image.
 *
 * `revealOnHover` is the desktop-card behaviour: hidden until the pointer is over the `group` ancestor, always visible
 * below the `md` breakpoint (a touch surface has no hover to reveal on). Off, the arrow is always visible.
 *
 * Centring rides `transform` on the control itself: `ButtonWithIcon` does not press with a transform, so nothing
 * overwrites it mid-click.
 */
export function CarouselNavButton({ onClick, side, ariaLabel, iconName, revealOnHover = false, style }: CarouselNavButtonProps) {
	const sideStyle = side === 'left' ? { left: spacing[2] } : { right: spacing[2], left: 'auto' };
	const revealClass = revealOnHover
		? ' opacity-100 md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto'
		: '';
	return (
		<ButtonWithIcon
			variant="glass"
			size="sm"
			icon={iconName}
			onClick={onClick}
			className={`rounded-full transition-opacity${revealClass}`}
			style={{
				...FLEX_CENTER_STYLE,
				position: 'absolute',
				top: '50%',
				transform: 'translateY(-50%)',
				backgroundColor: colors.overlay.glass,
				color: colors.text.secondary,
				border: 'none',
				borderRadius: borderRadius.full,
				width: spacing[8],
				height: spacing[8],
				cursor: 'pointer',
				backdropFilter: `blur(${modal.backdrop.blur})`,
				WebkitBackdropFilter: `blur(${modal.backdrop.blur})`,
				boxShadow: boxShadow.DEFAULT,
				transitionDuration: motion.duration.fast,
				...sideStyle,
				...style,
			}}
			aria-label={ariaLabel}
		/>
	);
}
